import { type ReactElement, useMemo, useState, CSSProperties } from 'react';
import { Status } from '@googlemaps/react-wrapper';
import { Select } from '@loadsmart/loadsmart-ui';
import type { SelectProps } from '@loadsmart/loadsmart-ui';
import type { Libraries } from '@googlemaps/js-api-loader';

import logger from '../utils/logger';
import to from '../utils/await-to';
import toArray from '../utils/to-array';

import { DATASOURCE_TYPE } from './location-select.constants';
import {
	generateGoogleMapsDatasource,
	getLocationDetails,
} from './location-select.helpers';
import type {
	FetchLocationOptions,
	LocationSelectProps,
	Location,
} from './location-select.types';
import { Wrapper } from './wrapper';
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable';

/**
 * TODO: add to doc
 * - use types cities for City/State locations
 * - use types regions for full addresses
 */
const DEFAULT_CONFIG: FetchLocationOptions = {
	types: 'cities',
	restrictions: { country: ['us', 'ca'] },
};

const DEFAULT_API_VERSION = '3.51';

const LIBRARIES: Libraries = ['places'];

const hiddenSelectStyle: CSSProperties = {
	visibility: 'hidden',
};

const getSelectedLocationDetails = async ({
	suggestions,
	config,
	compassConfig,
	query,
	shouldInferAmbiguousLocality,
	onInferLocality,
}: {
	suggestions: Selectable | Selectable[];
	config: LocationSelectProps['config'];
	compassConfig: LocationSelectProps['compassConfig'];
	query: string;
	shouldInferAmbiguousLocality: LocationSelectProps['shouldInferAmbiguousLocality'];
	onInferLocality: LocationSelectProps['onInferLocality'];
}): Promise<Selectable[]> => {
	const _suggestions = toArray(suggestions);

	const results = await Promise.all(
		_suggestions.map((suggestion) => {
			if (suggestion._type === DATASOURCE_TYPE) {
				return to(
					getLocationDetails(
						query,
						suggestion as Location,
						config,
						compassConfig,
						shouldInferAmbiguousLocality,
						onInferLocality,
					),
				);
			}

			return to(Promise.resolve(suggestion));
		}),
	);

	const locations: Selectable[] = results.reduce<Selectable[]>(
		(previousValue, [error, location], index) => {
			if (error) {
				logger.error('Failed to retrieve location details', { error, query });

				return previousValue;
			}

			if (!location) {
				const suggestion = _suggestions.at(index) as unknown as Location;

				logger.error('Failed to retrieve location details', {
					query,
					address: suggestion.address,
					place_id: suggestion.place_id,
				});

				return previousValue;
			}

			return previousValue.concat(location as Selectable);
		},
		[],
	);

	return locations;
};

/**
 * Miranda `Select` enabled with Google Autocomplete, Places, and Geocoder capabilities.
 *
 * The Google Maps datasource will always be included. You can customize how it fetches locations, if needed, by
 * providing the `config` prop. This is the default `config`:
 *
 * ```js
 * const DEFAULT_CONFIG = {
 *  types: 'cities',
 *  restrictions: { country: ['us', 'ca'] },
 * }
 * ```
 *
 * **Important**:
 * - You need to provide the `googleMapsAPIKey` prop in order for the `Select` to be visible and ready for location selection.
 * - If you add a custom datasource, make sure its reference its stable (i.e., you won't generate a new reference
 *   unnecessarily) so the `Select` can appropriately optimize its data source management.
 * - If you add a custom `config` for the Google Maps datasource, make sure its reference its stable (i.e., you won't
 *  generate a new reference unnecessarily) so the `Select` can appropriately optimize its data source management.
 * - If you need to accept more combined location types, add them to `MULTIPLE_LOCATION_TYPES_LIST` following the Places API
 *  restrictions that can be found here: https://developers.google.com/maps/documentation/javascript/supported_types
 *
 * ## Mocking the returned locations
 *
 * ```js
 * import { GoogleMapsMock, GoogleMapsMockFactory } from '@loadsmart/react-location-select/dist/testing';
 *
 * // Save the original google reference to reset it after the test is completed
 * const originalWindowGoogle = window.google;
 *
 * // Generate mock data using a default info.
 * // e.g. { "address": "New York, NY, United States" }
 * window.google = GoogleMapsMock();
 *
 * // Or generate mock data with an overriden info.
 * window.google = GoogleMapsMockFactory( {
 * 	predictionStub( query ) {
 * 		return new Promise( ( resolve ) => {
 * 			resolve( { predictions: [] } );
 * 		} );
 * 	},
 * 	geocodeStub( query, callback ) {
 * 		callback( ... );
 * 	},
 * 	placesStub( query, callback ) {
 * 		callback( ... );
 * 	},
 * } );
 *
 * // Reset the mock after the test is completed.
 * afterAll( () => {
 *   window.google = originalWindowGoogle;
 * } );
 *
 * ```
 *
 * ## Using the logger
 *
 * If you need to debug any behavior related to the LocationSelect when consuming the `@loadsmart/react-location-select` library,
 * you can enable component logging by setting the global variable `window.__LOCATION_SELECT_ENABLE_LOGGING__`.
 *
 * In the browser console, run the following command:
 *
 * ```js
 * window.__LOCATION_SELECT_ENABLE_LOGGING__ = true
 * ```
 */
export const LocationSelect = (
	props: LocationSelectProps,
): ReactElement<LocationSelectProps, typeof Select> | null => {
	const {
		googleMapsAPIKey,
		datasources: datasourcesProp,
		onChange,
		onQueryChange,
		onInferLocality,
		shouldInferAmbiguousLocality,
		config,
		compassConfig,
		multiple = false,
		disableMapsLoading = false,
		googleMapsAPIVersion = DEFAULT_API_VERSION,
		...rest
	} = props;

	const [query, setQuery] = useState('');
	const datasources = useMemo(() => {
		return [
			...(datasourcesProp ?? []),
			generateGoogleMapsDatasource({ ...DEFAULT_CONFIG, ...config }),
		];
	}, [datasourcesProp, config]);

	const handleChange: NonNullable<SelectProps['onChange']> = (event) => {
		if (!onChange) {
			return;
		}

		const {
			target: { value: suggestions },
		} = event;

		if (suggestions == null) {
			onChange(event);
			return;
		}

		getSelectedLocationDetails({
			suggestions,
			config,
			compassConfig,
			query,
			shouldInferAmbiguousLocality,
			onInferLocality,
		})
			.then((locations) => {
				onChange({
					...event,
					target: {
						...event.target,
						value: multiple ? locations : locations[0],
					},
				});

				return undefined;
			})
			.catch((error) => {
				logger.error(error);
			});
	};

	const handleQueryChange: SelectProps['onQueryChange'] = (event) => {
		setQuery(event.target.value);

		if (onQueryChange) {
			onQueryChange(event);
		}
	};

	return (
		<Wrapper
			disableMapsLoading={disableMapsLoading}
			apiKey={googleMapsAPIKey}
			libraries={LIBRARIES}
			language="en"
			version={googleMapsAPIVersion}
			render={(status): ReactElement => {
				return (
					<Select
						{...rest}
						style={status !== Status.SUCCESS ? hiddenSelectStyle : undefined}
						multiple={multiple}
						onQueryChange={handleQueryChange}
						onChange={handleChange}
						datasources={datasources}
					/>
				);
			}}
		/>
	);
};
